/** Small parts the administration screens share, kept out of the shared primitives on purpose:
 * nothing here restyles `Panel`, `KpiTile` or the tablist, it only arranges them.
 *
 * - `Facts` is a row of `KpiTile`s. A fact is **present or absent, never zero**: a caller that has
 *   not read the thing a tile counts passes no tile, so a failed read can never print a `0`.
 * - `AddPanel` is the "Add a company" disclosure: the creating form stays in the DOM, so every
 *   `data-testid` on it is findable, and opens when the button is pressed. A native `<details>`
 *   keeps keyboard and screen-reader behaviour without a line of ARIA.
 * - `BasisAndLimits` is where a long honest explanation goes when it is not the first thing a
 *   reader needs. **Ranked, never deleted** (design rule): the sentence is one click away.
 */

import { Plus } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { KpiTile } from "../components/KpiTile";
import { LazyDetails } from "../components/ui/LazyDetails";
import styles from "./ScreenParts.module.css";

export interface FactSpec {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly unit?: string;
  readonly note?: string;
  readonly textual?: boolean;
}

export function Facts({ facts, label, columns = 4 }: { facts: readonly FactSpec[]; label: string; columns?: 3 | 4 }) {
  if (facts.length === 0) return null;
  return (
    <div
      role="group"
      className={[styles.facts, columns === 3 ? styles.facts3 : ""].filter(Boolean).join(" ")}
      aria-label={label}
      data-testid="screen-facts"
    >
      {facts.map((fact) => (
        <KpiTile
          key={fact.key}
          kpi={fact.key}
          label={fact.label}
          value={fact.value}
          unit={fact.unit}
          note={fact.note}
          textual={fact.textual}
        />
      ))}
    </div>
  );
}

export function AddPanel({
  label,
  children,
  testId,
  defaultOpen = false,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
  /** Open on arrival. For a form that is the whole point of the place it sits in (a drawer opened to record
   * something), where a closed disclosure hid the owner field until a second click (G-P3-01). */
  defaultOpen?: boolean;
}) {
  return (
    <details className={styles.add} data-testid={testId} open={defaultOpen || undefined}>
      <summary className={styles.disclosureSummary}>
        <Plus size={14} weight="bold" aria-hidden="true" className={styles.caret} />
        {label}
      </summary>
      <div className={styles.disclosureBody}>{children}</div>
    </details>
  );
}

export function BasisAndLimits({
  children,
  testId,
  lazy = false,
}: {
  children: ReactNode;
  testId?: string;
  /** The body is not in the document while closed (`LazyDetails`): opt in per screen. */
  lazy?: boolean;
}) {
  if (lazy) {
    return (
      <LazyDetails
        className={styles.basis}
        testId={testId}
        summaryClassName={`${styles.disclosureSummary} ${styles.basisSummary}`}
        summary="Basis and limits"
      >
        <div className={styles.disclosureBody}>{children}</div>
      </LazyDetails>
    );
  }
  return (
    <details className={styles.basis} data-testid={testId}>
      <summary className={`${styles.disclosureSummary} ${styles.basisSummary}`}>
        Basis and limits
      </summary>
      <div className={styles.disclosureBody}>{children}</div>
    </details>
  );
}

